import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Maximize2, Minimize2 } from 'lucide-react';
import { useAgendaDock } from '@/components/layout/AgendaDockContext';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'wide';
  /** Ações opcionais no cabeçalho, à esquerda do X (ex.: setas de navegação). */
  headerActions?: ReactNode;
  /**
   * Região FIXA logo abaixo do título — não rola com o conteúdo (ex.: dados
   * do paciente no editor de documentos).
   */
  subHeader?: ReactNode;
  /**
   * Altura FIXA (85vh) em vez de crescer com o conteúdo. Padroniza o tamanho
   * do pop-up entre registros diferentes; nesse modo só o miolo rola —
   * `subHeader`/`footer` ficam parados. Sem `footer`, o `children` deve ocupar
   * a altura com flex e gerenciar o próprio scroll (o wrapper vira coluna flex
   * sem overflow). Default: false — cresce até `max-h-[90vh]` e rola tudo.
   */
  fixedHeight?: boolean;
  /**
   * Barra FIXA no rodapé do pop-up — não rola com o conteúdo (ex.: botões
   * Salvar / Imprimir / Fechar). Quando presente com `fixedHeight`, o miolo
   * (`children`) vira a área rolável e o footer fica sempre visível embaixo.
   */
  footer?: ReactNode;
  /**
   * Mostra no cabeçalho (à esquerda do X) um botão que expande o pop-up pra
   * TELA CHEIA e volta ao tamanho normal. Útil onde o conteúdo é longo — ex.:
   * o editor de documentos salvos. O estado fica no próprio Modal e sobrevive
   * a fechar/reabrir enquanto a tela não for trocada.
   */
  maximizable?: boolean;
  /**
   * Abre já MAXIMIZADO (tela cheia). O botão de restaurar continua no
   * cabeçalho pra voltar ao tamanho normal. Só faz sentido com `maximizable`.
   */
  defaultMaximized?: boolean;
}

/**
 * O Modal renderiza via portal no <body> — cliques dentro dele NÃO são
 * descendentes DOM de quem o abriu. Gavetas/painéis que fecham por
 * "clique-fora" (contains no próprio ref) devem ignorar mousedowns vindos
 * de um modal aberto, senão fecham — e desmontam o modal junto.
 */
export function isInsideModalPortal(node: EventTarget | null): boolean {
  if (!(node instanceof Node)) return false;
  const el = node instanceof Element ? node : node.parentElement;
  return !!el?.closest('[data-modal-portal]');
}

export const Modal = ({ isOpen, onClose, title, children, maxWidth = 'md', headerActions, subHeader, fixedHeight = false, footer, maximizable = false, defaultMaximized = false }: ModalProps) => {
  const [maximized, setMaximized] = useState(defaultMaximized);
  // Largura que a "Agenda do dia" está ocupando à direita quando fica encaixada
  // ("Manter sempre aberta"). Maximizar tem que PARAR antes dela — senão os
  // botões do cabeçalho e do rodapé do pop-up ficam escondidos atrás da agenda.
  // Fora do provider (ou com a agenda fechada) o valor é "0px" e nada muda.
  const { dockWidth } = useAgendaDock();
  const reserveRight = maximized && dockWidth !== '0px' ? dockWidth : null;
  // Só fecha pelo backdrop quando o gesto COMEÇA e TERMINA nele. Evita
  // fechamento acidental por: arrastar de dentro pra fora (seleção de
  // texto), e cliques sintetizados de toque que caem no backdrop após a
  // camada tocada mudar de lugar (teclado fechando, sheet animando etc.).
  const backdropPressRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; }
  }, [isOpen]);

  if (!isOpen) return null;

  const maxWidths = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-xl',
    xl: 'max-w-3xl',
    // `2xl` foi adicionado pra acomodar o wizard binocular de Pedidos
    // de LC (Sprint binocular), onde duas colunas OD|OE de SPH/CYL/AXIS/
    // ADD + sugestões precisam coexistir. max-w-5xl ≈ 1024px — confortável
    // em monitores ≥ 1200px e degrada com scroll horizontal abaixo disso.
    '2xl': 'max-w-5xl',
    // `wide` — duas colunas lado a lado (Solicitações no visual novo, spec 017).
    wide: 'max-w-[1400px]',
  };

  // Portal no <body>: ancestrais com `transform` (ex.: o painel "Texto
  // extraído" da 3ª coluna, que anima com translate) viram containing block
  // de `position: fixed` — sem o portal, o pop-up era posicionado/recortado
  // DENTRO do painel em vez de cobrir a viewport.
  return createPortal(
    <div
      data-modal-portal=""
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // O respiro padrão do container é p-4 (1rem); maximizado com a agenda
      // encaixada, o lado direito passa a reservar também a largura dela.
      // À esquerda, a coluna do "Ditar documentos" aberta (`--voice-dock-w`,
      // 0px quando fechada/recolhida): o pop-up abre AO LADO dela, e ela
      // segue clicável (parar o ao vivo, conferir o card) com o pop-up aberto.
      style={{
        left: 'var(--voice-dock-w, 0px)',
        ...(reserveRight ? { paddingRight: `calc(1rem + ${reserveRight})` } : {}),
      }}
    >
      {/* Camada de clique-fora, TRANSPARENTE: a página atrás continua
          visível, sem véu nem desfoque, como os cartões do Google Agenda.
          Ela ainda existe (e ainda fecha ao clicar fora) — só não pinta
          nada. Quem separa o pop-up do fundo agora é a sombra `shadow-float`. */}
      <div
        className="absolute inset-0 bg-transparent"
        onPointerDown={(e) => { backdropPressRef.current = e.target === e.currentTarget; }}
        onClick={(e) => {
          if (backdropPressRef.current && e.target === e.currentTarget) onClose();
          backdropPressRef.current = false;
        }}
      />
      {/* Maximizado: ignora o `maxWidth` e a altura padrão — ocupa toda a área
          útil (a viewport menos o respiro de 1rem do container). */}
      <div className={`relative bg-surface-container-highest shadow-float rounded-xl p-6 w-full animate-slide-up border border-outline-variant/40 flex flex-col ${
        maximized ? 'max-w-none h-full' : `${maxWidths[maxWidth]} ${fixedHeight ? 'h-[85vh]' : 'max-h-[90vh]'}`
      }`}>
        <div className={`flex justify-between items-center shrink-0 gap-3 ${subHeader ? 'mb-3' : 'mb-6'}`}>
          {title && <h2 className="text-xl font-semibold tracking-tight truncate">{title}</h2>}
          <div className="flex items-center gap-1.5 shrink-0 ml-auto">
            {headerActions}
            {maximizable && (
              <button
                type="button"
                onClick={() => setMaximized((v) => !v)}
                title={maximized ? 'Restaurar tamanho normal' : 'Maximizar (tela cheia)'}
                aria-label={maximized ? 'Restaurar tamanho normal' : 'Maximizar'}
                aria-pressed={maximized}
                className="p-1 rounded-full hover:bg-surface-bright text-on-surface-variant hover:text-on-surface transition-colors"
              >
                {maximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
            )}
            <button onClick={onClose} className="p-1 rounded-full hover:bg-surface-bright text-on-surface-variant hover:text-on-surface transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>
        {/* Sub-cabeçalho fixo (não rola) — ex.: dados do paciente. */}
        {subHeader && <div className="shrink-0 mb-4">{subHeader}</div>}
        {/* Miolo:
            · altura fixa + footer → área rolável (o footer fica parado embaixo);
            · altura fixa sem footer → coluna flex (o filho gerencia o scroll e
              o próprio rodapé, ex.: DocumentDetailView em fillHeight);
            · sem altura fixa → rola o bloco inteiro como antes. */}
        <div
          className={
            fixedHeight
              ? footer
                ? 'flex-1 min-h-0 overflow-y-auto pr-2 custom-scrollbar'
                : 'flex-1 min-h-0 flex flex-col'
              : 'overflow-y-auto pr-2 custom-scrollbar'
          }
        >
          {children}
        </div>
        {/* Rodapé fixo (não rola) — ex.: barra de botões. O conteúdo (EditorFooter)
            já traz seu próprio separador (border-t), então o wrapper é só shrink-0. */}
        {footer && <div className="shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};
